import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';


function AnimatedCardFlipContainer({
    duration = 500,

    CardFront,
    CardBack,
    isFlipped = false,

    borderRadius = '4px',
    recalculateHeightDelay = 250,
    
    ...otherProps
}) {
    const wrapperRef = useRef();
    const frontRef = useRef();
    const backRef = useRef();
    const [ heights, setHeights ] = useState(() => ({ front: undefined, back: undefined }));

    // reassess heights as soon as we start a flip
    useEffect(() => saveHeights(), [ isFlipped ]);

    // add event listeners for activity in the front and back cards that could cause their height to change
    useEffect(() => {
        wrapperRef.current?.addEventListener('click', wrapperWatcher);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', wrapperWatcher);
        }

        wrapperWatcher();

        return () => {
            wrapperRef.current?.removeEventListener('click', wrapperWatcher);
            if (typeof window !== 'undefined') {
                window?.removeEventListener('resize', wrapperWatcher);
            }
        };
    }, []);


    function wrapperWatcher() {
        setTimeout(() => saveHeights(), recalculateHeightDelay);
    }

    function saveHeights() {
        setHeights({
            front: frontRef.current?.offsetHeight,
            back: backRef.current?.offsetHeight,
        });
    }

    // if we don’t have values in `heights` use static positioning; allows initial render to have proper height
    const hasHeights = heights.front;   // if we have front, we also have back


    return (
        <Container
            duration={duration}
            borderRadius={borderRadius}
            {...otherProps}
        >
            <div
                ref={wrapperRef}
                className="card-wrapper rh-position-relative"
                style={{ height: (isFlipped ? heights.back : heights.front) ?? 'auto' }}
            >
                {/*
                    When initially rendered, we want the visible card to be `position: static`. The hidden card will be
                    `position: absolute`. Once we have the heights calculated, the visible card will also be made `absolute`,
                    which is required for the animation.

                    This way, on first render the height of the wrapping container will match the visible card, which it
                    would not if not cards started with `position: absolute`.
                */}
                <div
                    ref={frontRef}
                    className={classNames(CARD_COMMON_CSS_CLASSNAMES, 'card-front',
                        {
                            flipped: isFlipped,
                            'rh-position-absolute': hasHeights || isFlipped,
                        }
                    )}
                    aria-hidden={isFlipped}
                    data-test-name="card-front"
                >
                    {CardFront}
                </div>

                <div
                    ref={backRef}
                    className={classNames(CARD_COMMON_CSS_CLASSNAMES, 'card-back',
                        {
                            flipped: isFlipped,
                            'rh-position-absolute': hasHeights || !isFlipped,
                        }
                    )}
                    aria-hidden={!isFlipped}
                    data-test-name="card-back"
                >
                    {CardBack}
                </div>
            </div>
        </Container>
    );
}

AnimatedCardFlipContainer.propTypes = {
    duration: PropTypes.number,

    isFlipped: PropTypes.bool,              // flipped implies the back is showing
    CardFront: PropTypes.node.isRequired,
    CardBack: PropTypes.node.isRequired,

    borderRadius: PropTypes.string,
    recalculateHeightDelay: PropTypes.number,
};


const CARD_COMMON_CSS_CLASSNAMES = 'rh-width-100p rh-height-auto rh-box-sizing-border-box';

const Container = styled.div`
    .card-wrapper {
        transition: height ${props => props.duration}ms ease;
    }

    .card-front,
    .card-back {
        border-radius: ${props => props.borderRadius};
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        transition: transform ${props => props.duration}ms ease;
        backface-visibility: hidden;
    }

    .card-front {
        transform: perspective(1000px) rotateY(0);
    }
    .card-front.flipped {
        transform: perspective(1000px) rotateY(-180deg);
    }

    .card-back {
        transform: perspective(1000px) rotateY(180deg);
    }
    .card-back.flipped {
        transform: perspective(1000px) rotateY(0);
    }
`;


export default AnimatedCardFlipContainer;
